import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Draggable from 'react-draggable';
import { IconButton, Tooltip } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { XtramBadge, XtramButton, XtramSearch } from '../XtramModal/XtramModal';
import {
    downloadVatTransactionsExcel,
    downloadVatTransactionsPdf,
    prepareVatTransactionsExportTable,
} from '../../../Utils/vatTransactionsExport';
import BulkEditModal from '../../PrimaryBulkActionsToolbar/BulkEditModal';
import DocumentService from '../../../Services/documents.service';
import Constants from '../../../assests/Constants/constants';
import { beautifyNumber, parseBeautifiedNumber } from '../../../Utils/FormatNumber';
import styles from './HtmlViewerModal.module.scss';

// Column order matches BuildVatTransactionDocumentationHtml.
const VAT_DETAIL_COLUMN_TYPES = [
    'number', // תנועה
    'string', // חן חובה
    'string', // חן זכות
    'date',   // תאריך
    'string', // פרטים
    'string', // אסמכתא
    'number', // סה״כ סכום
    'number', // אחוז מע״מ
    'number', // סכום נטו
    'number', // מ.ע.מ
    'month',  // ח.דיווח
    'string', // קוד מיון
];

const REPORTING_MONTH_COLUMN = 10;

/** Data-column indexes in BuildVatTransactionDocumentationHtml (before chrome columns). */
const DATA_TOTAL_COL = 6;
const DATA_NET_COL = 8;
const DATA_VAT_COL = 9;

const EMPTY_TOTALS = { count: 0, total: 0, net: 0, vat: 0 };

const entryTypeToDocType = (type) => {
    const typeStr = String(type);
    if (typeStr === String(Constants.EntryType.Expense)) return 'Expenses';
    if (typeStr === String(Constants.EntryType.JournalEntry)) return 'JournalEntries';
    if (typeStr === String(Constants.EntryType.Income)) return 'Incomes';
    return null;
};

const rowKey = (id, type) => `${type}:${id}`;

/** Same type, or JournalEntries mixed with Expenses / Incomes (not Expenses+Incomes). */
const isAllowedBulkSelection = (docTypes) => {
    if (docTypes.size === 0) return false;
    if (docTypes.size === 1) return true;
    if (docTypes.size === 2 && docTypes.has('JournalEntries')) {
        return docTypes.has('Expenses') || docTypes.has('Incomes');
    }
    return false;
};

/** Prefer Expenses/Incomes for BulkEditModal field layout when mixed with journal entries. */
const resolveBulkEditModalType = (docTypes) => {
    if (docTypes.has('Expenses')) return 'Expenses';
    if (docTypes.has('Incomes')) return 'Incomes';
    if (docTypes.has('JournalEntries')) return 'JournalEntries';
    return null;
};

const readDataAmount = (row, dataColIndex, leadingColumns) => {
    const cell = row?.children?.[leadingColumns + dataColIndex];
    const n = parseBeautifiedNumber(cell?.textContent || '');
    return Number.isFinite(n) ? n : 0;
};

const sumDataRows = (rows, leadingColumns) => (
    rows.reduce((acc, row) => ({
        count: acc.count + 1,
        total: acc.total + readDataAmount(row, DATA_TOTAL_COL, leadingColumns),
        net: acc.net + readDataAmount(row, DATA_NET_COL, leadingColumns),
        vat: acc.vat + readDataAmount(row, DATA_VAT_COL, leadingColumns),
    }), { ...EMPTY_TOTALS })
);

const collectVisibleDataRows = (root) => {
    if (!root) return [];
    return Array.from(root.querySelectorAll('tbody tr.tr[data-id]')).filter((row) => !row.hidden);
};

function cellSortValue(row, index, type) {
    const cell = row?.children?.[index];
    if (!cell) return null;
    const text = (cell.textContent || '').replace(/\u00a0/g, ' ').trim();
    if (!text) return null;

    if (type === 'number') {
        const negative = text.startsWith('(') && text.endsWith(')');
        const cleaned = text.replace(/[()\s]/g, '').replace(/,/g, '');
        const value = Number(cleaned);
        if (Number.isNaN(value)) return null;
        return negative ? -value : value;
    }

    if (type === 'date') {
        const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (!match) return null;
        return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1])).getTime();
    }

    if (type === 'month') {
        const match = text.match(/^(\d{1,2})\/(\d{4})$/);
        if (!match) return null;
        return new Date(Number(match[2]), Number(match[1]) - 1, 1).getTime();
    }

    return text;
}

function compareSortValues(a, b, type, direction) {
    const aEmpty = a == null || a === '';
    const bEmpty = b == null || b === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    let result = 0;
    if (type === 'number' || type === 'date' || type === 'month') {
        result = a - b;
    } else {
        result = String(a).localeCompare(String(b), 'he', { numeric: true, sensitivity: 'base' });
    }
    return direction === 'desc' ? -result : result;
}

function sectionMonthValue(section) {
    const fromRow = cellSortValue(section.rows[0], REPORTING_MONTH_COLUMN, 'month');
    if (fromRow != null) return fromRow;
    const headerText = section.header?.textContent || '';
    const match = headerText.match(/(\d{1,2})\/(\d{4})/);
    if (!match) return null;
    return new Date(Number(match[2]), Number(match[1]) - 1, 1).getTime();
}

function sortVatDetailsTable(table, columnIndex, direction) {
    const tbody = table.querySelector('tbody');
    if (!tbody) return;

    const type = VAT_DETAIL_COLUMN_TYPES[columnIndex] || 'string';
    const children = Array.from(tbody.children);
    const monthFooters = children.filter((row) => row.classList.contains('tr-final-row'));
    const grandTotal = monthFooters[monthFooters.length - 1] || null;

    const sections = [];
    let current = null;

    children.forEach((row) => {
        if (row === grandTotal) return;
        if (row.classList.contains('month-header')) {
            current = { header: row, rows: [], footer: null };
            sections.push(current);
            return;
        }
        if (row.classList.contains('tr-final-row')) {
            if (current) current.footer = row;
            return;
        }
        if (row.classList.contains('tr') && row.hasAttribute('data-id')) {
            if (!current) {
                current = { header: null, rows: [], footer: null };
                sections.push(current);
            }
            current.rows.push(row);
        }
    });

    if (columnIndex === REPORTING_MONTH_COLUMN) {
        sections.sort((a, b) => compareSortValues(
            sectionMonthValue(a),
            sectionMonthValue(b),
            'month',
            direction
        ));
    }

    sections.forEach((section) => {
        section.rows.sort((a, b) => compareSortValues(
            cellSortValue(a, columnIndex, type),
            cellSortValue(b, columnIndex, type),
            type,
            direction
        ));
    });

    const fragment = document.createDocumentFragment();
    sections.forEach((section) => {
        if (section.header) fragment.appendChild(section.header);
        section.rows.forEach((row) => fragment.appendChild(row));
        if (section.footer) fragment.appendChild(section.footer);
    });
    if (grandTotal) fragment.appendChild(grandTotal);
    tbody.appendChild(fragment);
}

function applyVatDetailsSort(root, sort) {
    const table = root.querySelector('table.table');
    if (!table) return;

    const headers = table.querySelectorAll('thead th');
    headers.forEach((header, index) => {
        const label = (header.textContent || '').trim();
        const active = sort?.index === index;
        header.classList.toggle('sorted', active);
        header.title = 'מיון';
        header.replaceChildren();
        if (active) {
            const indicator = document.createElement('span');
            indicator.className = 'sort-ind';
            indicator.textContent = sort.direction === 'desc' ? '▼' : '▲';
            header.appendChild(indicator);
        }
        header.appendChild(document.createTextNode(label));
    });

    if (!sort) return;

    const hint = root.querySelector('.det-small');
    const label = headers[sort.index]?.textContent?.replace(/[▲▼]/g, '').trim();
    if (hint && label) {
        const directionLabel = sort.direction === 'desc' ? 'יורד' : 'עולה';
        hint.textContent = `ממוין לפי ${label} (${directionLabel})`;
    }

    sortVatDetailsTable(table, sort.index, sort.direction);
}

/**
 * Injects an optional select checkbox column and a running # column in front of every transaction row.
 * Returns how many data rows there are.
 */
function decorateTransactionTable(root, { withSelection = false } = {}) {
    const table = root.querySelector('table.table');
    if (!table) return 0;

    const leadingColumns = withSelection ? 2 : 1;
    const headRow = table.querySelector('thead tr');
    if (headRow) {
        if (withSelection) {
            const selectTh = document.createElement('th');
            selectTh.className = 'row-select';
            const selectAll = document.createElement('input');
            selectAll.type = 'checkbox';
            selectAll.className = 'select-all';
            selectAll.title = 'Select all';
            selectTh.appendChild(selectAll);
            headRow.insertBefore(selectTh, headRow.firstChild);
        }

        const numTh = document.createElement('th');
        numTh.className = 'row-num';
        numTh.textContent = '#';
        headRow.insertBefore(numTh, withSelection ? headRow.children[1] : headRow.firstChild);
    }

    let count = 0;
    table.querySelectorAll('tbody > tr').forEach((row) => {
        if (row.classList.contains('month-header')) {
            const cell = row.firstElementChild;
            if (cell) cell.colSpan = (cell.colSpan || 1) + leadingColumns;
            return;
        }

        const numTd = document.createElement('td');
        numTd.className = 'row-num';
        const selectTd = withSelection ? document.createElement('td') : null;
        if (selectTd) selectTd.className = 'row-select';

        if (row.classList.contains('tr') && row.hasAttribute('data-id')) {
            count += 1;
            numTd.textContent = String(count);
            if (selectTd) {
                const checkbox = document.createElement('input');
                checkbox.type = 'checkbox';
                checkbox.className = 'row-check';
                checkbox.dataset.id = row.getAttribute('data-id') || '';
                checkbox.dataset.type = row.getAttribute('data-type') || '';
                selectTd.appendChild(checkbox);
            }
        }

        row.insertBefore(numTd, row.firstChild);
        if (selectTd) row.insertBefore(selectTd, row.firstChild);
    });
    return count;
}

const normalize = (text) => (text || '').replace(/\u00a0/g, ' ').toLowerCase();

/**
 * Hides the transaction rows that don't contain every word of `query` (amounts match with or without
 * thousands separators) and renumbers the visible ones. While filtering, totals and empty months are
 * hidden because they describe the full list. Returns how many rows are visible.
 */
function applySearch(root, query) {
    const table = root.querySelector('table.table');
    if (!table) return 0;

    const terms = normalize(query).trim().split(/\s+/).filter(Boolean);
    const filtering = terms.length > 0;
    const setHidden = (row, hidden) => {
        row.hidden = hidden;
        row.classList.toggle('is-hidden', hidden);
    };

    let visible = 0;
    let monthHeader = null;
    let monthMatches = 0;
    const closeMonth = () => {
        if (monthHeader) setHidden(monthHeader, filtering && monthMatches === 0);
    };

    table.querySelectorAll('tbody > tr').forEach((row) => {
        if (row.classList.contains('month-header')) {
            closeMonth();
            monthHeader = row;
            monthMatches = 0;
            return;
        }
        if (!(row.classList.contains('tr') && row.hasAttribute('data-id'))) {
            setHidden(row, filtering);
            return;
        }
        const text = normalize(Array.from(row.children)
            .filter((cell) => !cell.classList.contains('row-num') && !cell.classList.contains('row-select'))
            .map((cell) => cell.textContent)
            .join(' '));
        const haystack = `${text} ${text.replace(/,/g, '')}`;
        const match = !filtering || terms.every((term) => haystack.includes(term));
        setHidden(row, !match);
        if (match) {
            visible += 1;
            monthMatches += 1;
            const numCell = row.querySelector('td.row-num');
            if (numCell) numCell.textContent = String(visible);
        }
    });
    closeMonth();
    return visible;
}

function syncSelectionUi(root, selectedMap) {
    if (!root) return;
    let visibleChecked = 0;
    let visibleTotal = 0;

    root.querySelectorAll('tbody tr.tr[data-id]').forEach((row) => {
        if (row.hidden) return;
        visibleTotal += 1;
        const id = row.getAttribute('data-id');
        const type = row.getAttribute('data-type');
        const key = rowKey(id, type);
        const checked = selectedMap.has(key);
        row.classList.toggle('is-selected', checked);
        const checkbox = row.querySelector('input.row-check');
        if (checkbox) checkbox.checked = checked;
        if (checked) visibleChecked += 1;
    });

    const selectAll = root.querySelector('thead input.select-all');
    if (selectAll) {
        selectAll.checked = visibleTotal > 0 && visibleChecked === visibleTotal;
        selectAll.indeterminate = visibleChecked > 0 && visibleChecked < visibleTotal;
    }
}

const formatMonth = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const HtmlViewerModal = ({
    open = false,
    title,
    onClose = () => { },
    htmlContent = '',
    onRowClick = () => { },
    vatRequestDto,
    token,
    client,
    accountCardOptions = [],
    sortCodeIdOptions = [],
    onBulkSaved,
    enableBulkEdit = false,
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const nodeRef = useRef(null);
    const contentRef = useRef(null);
    const selectedRef = useRef(new Map());
    const [sortState, setSortState] = useState(null);
    const [rowCount, setRowCount] = useState(0);
    const [visibleCount, setVisibleCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [exporting, setExporting] = useState(null);
    const [selectedCount, setSelectedCount] = useState(0);
    const [selectedTotals, setSelectedTotals] = useState(EMPTY_TOTALS);
    const [processedTotals, setProcessedTotals] = useState(EMPTY_TOTALS);
    const [unprocessedTotals, setUnprocessedTotals] = useState(EMPTY_TOTALS);
    const [bulkEditOpen, setBulkEditOpen] = useState(false);
    const [bulkSaving, setBulkSaving] = useState(false);
    const [selectedSnapshot, setSelectedSnapshot] = useState([]);
    const sort = sortState?.key === htmlContent ? sortState : null;

    const bulkEditEnabled = enableBulkEdit && !!client && !!token;
    const leadingColumns = bulkEditEnabled ? 2 : 1;

    const formattedAccountCardOptions = useMemo(() => (
        (accountCardOptions || [])
            .filter((option) => option && option.cardId != null && option.value1 !== 'AddItem')
            .map((option) => ({
                value: option.cardId,
                label: option.accountCaseId
                    ? `${option.name} - ${option.accountCaseId || ''}`
                    : `${option.name || ''}`,
                name: option.name,
                accountCaseId: option.accountCaseId || '',
                cardId: option.cardId,
                value1: option.value1,
                palCode: option.palCode,
                isEditable: option.isEditable,
            }))
    ), [accountCardOptions]);

    const refreshTotals = () => {
        const rows = collectVisibleDataRows(contentRef.current);
        const processed = [];
        const unprocessed = [];
        const selected = [];
        rows.forEach((row) => {
            if (row.classList.contains('missing-row')) unprocessed.push(row);
            else processed.push(row);
            const key = rowKey(row.getAttribute('data-id'), row.getAttribute('data-type'));
            if (selectedRef.current.has(key)) selected.push(row);
        });
        setProcessedTotals(sumDataRows(processed, leadingColumns));
        setUnprocessedTotals(sumDataRows(unprocessed, leadingColumns));
        setSelectedTotals(sumDataRows(selected, leadingColumns));
        setSelectedCount(selectedRef.current.size);
    };

    const clearSelection = () => {
        selectedRef.current = new Map();
        syncSelectionUi(contentRef.current, selectedRef.current);
        refreshTotals();
    };

    const publishSelection = () => {
        syncSelectionUi(contentRef.current, selectedRef.current);
        refreshTotals();
    };

    useLayoutEffect(() => {
        const root = contentRef.current;
        if (!root) return;
        root.innerHTML = htmlContent || '';
        if (!htmlContent) {
            setRowCount(0);
            return;
        }
        applyVatDetailsSort(root, sort);
        setRowCount(decorateTransactionTable(root, { withSelection: bulkEditEnabled }));
        syncSelectionUi(root, selectedRef.current);
        refreshTotals();
    }, [htmlContent, sort, open, bulkEditEnabled]);

    // Runs after the rebuild above (same deps plus the query), so a refresh or re-sort keeps the filter.
    useLayoutEffect(() => {
        const root = contentRef.current;
        if (!root || !htmlContent) {
            setVisibleCount(0);
            setProcessedTotals(EMPTY_TOTALS);
            setUnprocessedTotals(EMPTY_TOTALS);
            setSelectedTotals(EMPTY_TOTALS);
            return;
        }
        setVisibleCount(applySearch(root, searchQuery));
        syncSelectionUi(root, selectedRef.current);
        refreshTotals();
    }, [htmlContent, sort, open, searchQuery, bulkEditEnabled, leadingColumns]);

    useEffect(() => {
        setSearchQuery('');
        clearSelection();
        setBulkEditOpen(false);
    }, [open, vatRequestDto?.vatType]);

    useEffect(() => {
        clearSelection();
    }, [htmlContent]);

    const bulkDocTypes = useMemo(() => {
        const types = new Set(
            selectedSnapshot.map((item) => entryTypeToDocType(item.type)).filter(Boolean)
        );
        return types;
    }, [selectedSnapshot]);

    const bulkDocType = resolveBulkEditModalType(bulkDocTypes) || 'Expenses';
    const bulkIds = selectedSnapshot.map((item) => item.id);

    if (!open) return null;

    const handleRowClick = (e) => {
        const tr = e.target.closest('tr.tr');
        if (!tr || !tr.getAttribute('data-id')) return;
        const cells = Array.from(tr.querySelectorAll('td:not(.row-num):not(.row-select)')).map((cell) => cell.textContent);
        const id = tr.getAttribute('data-id');
        const type = tr.getAttribute('data-type');
        onRowClick({ cells, id, type });
    };

    const toggleRowSelection = (id, type, forceChecked) => {
        if (!bulkEditEnabled || !id) return;
        const key = rowKey(id, type);
        const nextChecked = forceChecked != null ? forceChecked : !selectedRef.current.has(key);
        if (nextChecked) selectedRef.current.set(key, { id, type });
        else selectedRef.current.delete(key);
        publishSelection();
    };

    const toggleSelectAllVisible = (checked) => {
        if (!bulkEditEnabled) return;
        const root = contentRef.current;
        if (!root) return;
        root.querySelectorAll('tbody tr.tr[data-id]').forEach((row) => {
            if (row.hidden) return;
            const id = row.getAttribute('data-id');
            const type = row.getAttribute('data-type');
            const key = rowKey(id, type);
            if (checked) selectedRef.current.set(key, { id, type });
            else selectedRef.current.delete(key);
        });
        publishSelection();
    };

    const handleContentClick = (e) => {
        const selectAll = e.target.closest('input.select-all');
        if (selectAll) {
            e.stopPropagation();
            toggleSelectAllVisible(selectAll.checked);
            return;
        }

        const rowCheck = e.target.closest('input.row-check');
        if (rowCheck) {
            e.stopPropagation();
            toggleRowSelection(rowCheck.dataset.id, rowCheck.dataset.type, rowCheck.checked);
            return;
        }

        // Clicking the select cell (not the input) should toggle without opening the row editor.
        const selectCell = e.target.closest('td.row-select, th.row-select');
        if (selectCell) {
            e.stopPropagation();
            const input = selectCell.querySelector('input.row-check, input.select-all');
            if (input?.classList.contains('select-all')) {
                input.checked = !input.checked;
                toggleSelectAllVisible(input.checked);
            } else if (input?.classList.contains('row-check')) {
                input.checked = !input.checked;
                toggleRowSelection(input.dataset.id, input.dataset.type, input.checked);
            }
            return;
        }

        const header = e.target.closest('thead th');
        if (header) {
            if (header.classList.contains('row-num') || header.classList.contains('row-select')) {
                setSortState(null);
                return;
            }
            const index = header.cellIndex - leadingColumns;
            setSortState((prev) => {
                const current = prev?.key === htmlContent ? prev : null;
                if (current?.index === index) {
                    return {
                        key: htmlContent,
                        index,
                        direction: current.direction === 'asc' ? 'desc' : 'asc',
                    };
                }
                return { key: htmlContent, index, direction: 'asc' };
            });
            return;
        }
        handleRowClick(e);
    };

    const period = [formatMonth(vatRequestDto?.startDate), formatMonth(vatRequestDto?.endDate)]
        .filter(Boolean)
        .join(' - ');

    const runExport = async (kind, { selectedOnly = false } = {}) => {
        const table = contentRef.current?.querySelector('table.table');
        if (!table || exporting) return;

        const selectedKeys = selectedOnly
            ? new Set(Array.from(selectedRef.current.keys()))
            : null;
        if (selectedOnly && (!selectedKeys || selectedKeys.size === 0)) {
            toast.info(t('PleaseSelectOptionForSelectedRows') || 'Select rows first');
            return;
        }

        setExporting(selectedOnly ? `${kind}-selected` : kind);
        try {
            const exportTitle = selectedOnly
                ? `${title} (${t('Selected') || 'Selected'})`
                : title;

            const prepared = prepareVatTransactionsExportTable(table, { selectedKeys });
            if (!prepared.table || (selectedOnly && prepared.rowCount === 0)) {
                toast.info(t('NoDataToDownload') || 'No data to download');
                return;
            }

            if (kind === 'excel') {
                downloadVatTransactionsExcel({
                    table: prepared.table,
                    title: exportTitle,
                    caseId: vatRequestDto?.caseId,
                    period,
                    amountColumns: prepared.amountColumns,
                });
            } else {
                await downloadVatTransactionsPdf({
                    html: htmlContent,
                    table: prepared.table,
                    title: exportTitle,
                    caseId: vatRequestDto?.caseId,
                    token,
                    // Table is already filtered/stripped; don't filter again inside PDF helper.
                    selectedKeys: null,
                });
            }
        } catch (err) {
            console.error('VAT transactions export failed:', err);
            toast.error(t('ExportFailed'));
        } finally {
            setExporting(null);
        }
    };

    const openBulkEdit = () => {
        const items = Array.from(selectedRef.current.values());
        if (items.length === 0) return;

        const docTypes = new Set(items.map((item) => entryTypeToDocType(item.type)).filter(Boolean));
        if (!isAllowedBulkSelection(docTypes)) {
            toast.warning(
                t('BulkEditMixedTypesNotAllowed')
                || 'Bulk edit supports one document type, or journal entries mixed with expenses/incomes'
            );
            return;
        }

        setSelectedSnapshot(items);
        setBulkEditOpen(true);
    };

    const handleBulkPatchDocuments = async (patchOrPatches) => {
        if (Array.isArray(patchOrPatches)) {
            toast.error(t('BatchSaveNotSupportedInBulkEdit') || 'Batch save is only supported in duplicate mode');
            return;
        }
        if (!selectedSnapshot.length) return;

        setBulkSaving(true);
        try {
            const {
                flipAmount,
                flipCards,
                replaceDescriptionWithAccountName,
                syncProject,
                projectId,
                alignRegistrationMonthToDocumentDate,
                ...patch
            } = patchOrPatches || {};

            const options = {
                flipAmount: !!flipAmount,
                flipCards: !!flipCards,
                replaceDescriptionWithAccountName: !!replaceDescriptionWithAccountName,
                syncProject: !!syncProject,
                projectId: syncProject ? (projectId ?? null) : undefined,
                alignRegistrationMonthToDocumentDate: !!alignRegistrationMonthToDocumentDate,
            };

            // Group by document type so mixed JournalEntries + Expenses/Incomes each hit the right API.
            const groups = selectedSnapshot.reduce((acc, item) => {
                const docType = entryTypeToDocType(item.type);
                if (!docType) return acc;
                if (!acc[docType]) acc[docType] = [];
                acc[docType].push(item.id);
                return acc;
            }, {});

            const results = await Promise.all(
                Object.entries(groups).map(([docType, ids]) =>
                    DocumentService.BulkUpdateDocuments(
                        docType,
                        ids,
                        patch,
                        client.caseId,
                        token,
                        options
                    )
                )
            );

            if (results.some((res) => !res.ok)) {
                toast.error(t('documentUpdateError'));
                return;
            }

            toast.success(t('documentUpdateSuccess'));
            setBulkEditOpen(false);
            clearSelection();
            if (onBulkSaved) await onBulkSaved();
        } catch (err) {
            console.error('Bulk edit from VAT HTML viewer failed:', err);
            toast.error(t('documentUpdateError'));
        } finally {
            setBulkSaving(false);
        }
    };

    const formatTotalsLabel = (label, totals) => (
        `${label}: ${totals.count} | ${t('Total') || 'Total'}: ${beautifyNumber(totals.total)} | ${t('NetAmount') || 'Net'}: ${beautifyNumber(totals.net)} | ${t('VAT') || 'VAT'}: ${beautifyNumber(totals.vat)}`
    );

    const showStatusTotals = unprocessedTotals.count > 0;
    const showSelectedTotals = selectedCount > 0;

    // Portaled so it stacks above the modal that opened it; the edit modal opened from a row mounts later and stays on top.
    return createPortal(
        <div className={styles.layer} dir={direction}>
            <Draggable handle=".drag-handle" nodeRef={nodeRef}>
                <div ref={nodeRef} className={`vat-html-viewer ${styles.panel}`}>
                    <div className={`drag-handle ${styles.header}`}>
                        <Tooltip title={t('Close')}>
                            <IconButton onClick={onClose} className={styles.closeBtn}>
                                <CancelIcon />
                            </IconButton>
                        </Tooltip>
                        <div className={styles.title}>{title}</div>
                        <span className={styles.headerSpacer} />
                    </div>

                    <div className={styles.toolbar}>
                        <div className={styles.toolbarInfo}>
                            <XtramSearch
                                direction={direction}
                                value={searchQuery}
                                onChange={setSearchQuery}
                                placeholder={t('Search')}
                                className={styles.search}
                            />
                            <XtramBadge tone={searchQuery && !visibleCount ? 'warning' : 'info'}>
                                {searchQuery
                                    ? `${t('TransactionsCount')}: ${visibleCount} / ${rowCount}`
                                    : `${t('TransactionsCount')}: ${rowCount}`}
                            </XtramBadge>
                            {bulkEditEnabled && selectedCount > 0 && (
                                <XtramBadge tone="info">
                                    {t('Selected') || 'Selected'}: {selectedCount}
                                </XtramBadge>
                            )}
                            {period && <span className={styles.period}>{period}</span>}
                        </div>
                        <div className={styles.toolbarActions}>
                            {bulkEditEnabled && (
                                <XtramButton
                                    variant="secondary"
                                    small
                                    disabled={selectedCount === 0 || bulkSaving}
                                    onClick={openBulkEdit}
                                >
                                    <EditOutlinedIcon sx={{ fontSize: 17 }} />
                                    {t('BulkEdit')}
                                    {selectedCount > 0 ? ` (${selectedCount})` : ''}
                                </XtramButton>
                            )}
                            {bulkEditEnabled && selectedCount > 0 && (
                                <>
                                    <XtramButton
                                        variant="secondary"
                                        small
                                        busy={exporting === 'excel-selected'}
                                        disabled={!!exporting}
                                        onClick={() => runExport('excel', { selectedOnly: true })}
                                    >
                                        <GridOnOutlinedIcon sx={{ fontSize: 17 }} />
                                        {t('ExportSelectedExcel') || 'Excel (selected)'}
                                    </XtramButton>
                                    <XtramButton
                                        variant="secondary"
                                        small
                                        busy={exporting === 'pdf-selected'}
                                        disabled={!!exporting}
                                        onClick={() => runExport('pdf', { selectedOnly: true })}
                                    >
                                        <PictureAsPdfOutlinedIcon sx={{ fontSize: 17 }} />
                                        {t('ExportSelectedPdf') || 'PDF (selected)'}
                                    </XtramButton>
                                </>
                            )}
                            <XtramButton
                                variant="secondary"
                                small
                                busy={exporting === 'excel'}
                                disabled={!visibleCount || !!exporting}
                                onClick={() => runExport('excel')}
                            >
                                <GridOnOutlinedIcon sx={{ fontSize: 17 }} />
                                {t('Excel')}
                            </XtramButton>
                            <XtramButton
                                variant="secondary"
                                small
                                busy={exporting === 'pdf'}
                                disabled={!visibleCount || !!exporting}
                                onClick={() => runExport('pdf')}
                            >
                                <PictureAsPdfOutlinedIcon sx={{ fontSize: 17 }} />
                                {t('PDF')}
                            </XtramButton>
                        </div>
                    </div>

                    {(showSelectedTotals || showStatusTotals) && (
                        <div className={styles.totalsBar} dir={direction}>
                            {showSelectedTotals && (
                                <div className={`${styles.totalsChip} ${styles.totalsSelected}`}>
                                    {formatTotalsLabel(t('SelectedRowsTotals') || 'Selected totals', selectedTotals)}
                                </div>
                            )}
                            {showStatusTotals && (
                                <>
                                    <div className={`${styles.totalsChip} ${styles.totalsProcessed}`}>
                                        {formatTotalsLabel(t('ProcessedRowsTotals') || 'Processed totals', processedTotals)}
                                    </div>
                                    <div className={`${styles.totalsChip} ${styles.totalsUnprocessed}`}>
                                        {formatTotalsLabel(t('UnprocessedRowsTotals') || 'Unprocessed totals', unprocessedTotals)}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    <div
                        ref={contentRef}
                        onClick={handleContentClick}
                        className={styles.content}
                    />
                </div>
            </Draggable>

            {bulkEditOpen && (
                <BulkEditModal
                    open={bulkEditOpen}
                    ids={bulkIds}
                    onClose={() => setBulkEditOpen(false)}
                    onSavePatch={handleBulkPatchDocuments}
                    formattedAccountCardOptions={formattedAccountCardOptions}
                    sortCodeIdOptions={sortCodeIdOptions}
                    client={client}
                    type={bulkDocType || 'Expenses'}
                    accountCardOptions={accountCardOptions}
                    token={token}
                />
            )}
        </div>,
        document.body
    );
};

export default HtmlViewerModal;
